import { collection, addDoc, query, where, getDocs, orderBy, serverTimestamp, doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase.config';

/**
 * Generates an elegant, readable Order Number (e.g. LHB-2609-X7K2)
 */
const generateOrderNumber = () => {
  const date = new Date();
  const yearSuffix = date.getFullYear().toString().slice(-2);
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const randomChars = Math.random().toString(36).substring(2, 6).toUpperCase();
  return `LHB-${yearSuffix}${month}-${randomChars}`;
};

/**
 * Creates and persists a new order document in Cloud Firestore
 */
export const createOrder = async ({
  userId,
  userEmail,
  customerName,
  phoneNumber,
  shippingAddress,
  cartItems,
  totalAmount,
  totalQty,
  paymentMethod = 'Cash on Delivery',
  couponCode = null,
  discountAmount = 0,
}) => {
  const orderNumber = generateOrderNumber();
  const orderData = {
    orderNumber,
    userId,
    userEmail,
    customerName,
    phoneNumber,
    shippingAddress,
    items: cartItems.map((item) => ({
      id: item.id,
      productName: item.productName,
      price: item.price,
      quantity: item.quantity,
      totalPrice: item.totalPrice,
      imgUrl: item.imgUrl || '',
    })),
    totalAmount,
    totalQty,
    couponCode: couponCode || null,
    discountAmount: Number(discountAmount) || 0,
    status: 'Pending', // Pending -> Processing -> Shipped -> Delivered
    paymentMethod,
    createdAt: new Date().toISOString(),
    timestamp: serverTimestamp(),
  };

  const docRef = await addDoc(collection(db, 'orders'), orderData);
  return { id: docRef.id, orderNumber, ...orderData };
};

/**
 * Retrieves all orders belonging to a specific customer, ordered newest first
 */
export const getUserOrders = async (userId) => {
  try {
    const ordersRef = collection(db, 'orders');
    // Query by userId
    const q = query(
      ordersRef,
      where('userId', '==', userId),
      orderBy('createdAt', 'desc')
    );

    const querySnapshot = await getDocs(q);
    const orders = [];
    querySnapshot.forEach((doc) => {
      orders.push({ id: doc.id, ...doc.data() });
    });
    return orders;
  } catch (error) {
    // If composite index is pending, fallback to sorting in-memory
    if (error.code === 'failed-precondition' || error.message?.includes('index')) {
      const fallbackQuery = query(
        collection(db, 'orders'),
        where('userId', '==', userId)
      );
      const snapshot = await getDocs(fallbackQuery);
      const orders = [];
      snapshot.forEach((doc) => {
        orders.push({ id: doc.id, ...doc.data() });
      });
      return orders.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    }
    throw error;
  }
};

/**
 * Cancels a user's pending order
 */
export const cancelUserOrder = async (orderId) => {
  try {
    const orderDocRef = doc(db, 'orders', orderId);
    await updateDoc(orderDocRef, {
      status: 'Cancelled',
      cancelledAt: new Date().toISOString(),
    });
    return true;
  } catch (error) {
    console.error('Error cancelling order:', error);
    throw error;
  }
};
